import type { QRCodeSettings } from './types';

/**
 * QR Code Color Schemes
 * Predefined color combinations for QR code customization
 */
export interface QRColorScheme {
	module: string;
	ring: string;
	center: string;
	bg: string;
}

export const QR_COLOR_SCHEMES: Record<string, QRColorScheme> = {
	'Major White': {
		module: '#ffffff',
		ring: '#ffffff',
		center: '#ffffff',
		bg: '#000000'
	},
	'Shane Noir': {
		module: '#000000',
		ring: '#333333',
		center: '#666666',
		bg: '#ffffff'
	},
	'Moody Blue': {
		module: '#0066cc',
		ring: '#003d7a',
		center: '#4d9fff',
		bg: '#ffffff'
	},
	'St Patrick Green': {
		module: '#1c7d43',
		ring: '#13532d',
		center: '#70c559',
		bg: '#ffffff'
	},
	'Dug Umber': {
		module: '#8B4513',
		ring: '#5D2F0A',
		center: '#CD853F',
		bg: '#ffffff'
	},
	// Supertip retro is a purple and dark blue theme
	'Supertip Retro': {
		module: '#6B46C1',
		ring: '#1E1B4B',
		center: '#A855F7',
		bg: '#ffffff'
	}
} as const;

/**
 * QR Code Style Options
 * Available style configurations for QR code customization
 */
export interface QRStyleOption<T = string> {
	value: T;
	label: string;
	icon: string;
}

export const QR_DOT_STYLES: Array<QRStyleOption<QRCodeSettings['dotStyle']>> = [
	{ value: 'square', label: 'Square', icon: '⬜' },
	{ value: 'dots', label: 'Dots', icon: '⚫' },
	{ value: 'rounded', label: 'Rounded', icon: '🔲' },
	{ value: 'extra-rounded', label: 'Extra Rounded', icon: '⭕' },
	{ value: 'classy', label: 'Classy', icon: '💎' },
	{ value: 'classy-rounded', label: 'Classy Rounded', icon: '💠' }
] as const;

export const QR_CORNER_STYLES: Array<QRStyleOption<QRCodeSettings['cornerSquareStyle']>> = [
	{ value: 'square', label: 'Square', icon: '⬜' },
	{ value: 'dot', label: 'Dot', icon: '⚫' },
	{ value: 'extra-rounded', label: 'Extra Rounded', icon: '⭕' }
] as const;

export const QR_GRADIENT_TYPES: Array<QRStyleOption<QRCodeSettings['gradientType']>> = [
	{ value: 'none', label: 'Solid Color', icon: '🎨' },
	{ value: 'linear', label: 'Linear Gradient', icon: '📐' },
	{ value: 'radial', label: 'Radial Gradient', icon: '⭕' }
] as const;

/**
 * Default QR Code Settings
 * Fallback configuration for QR code generation
 */
export const DEFAULT_QR_SETTINGS: QRCodeSettings = {
	moduleColor: '#ffffff',
	positionRingColor: '#ffffff',
	positionCenterColor: '#ffffff',
	backgroundColor: '#000000',
	dotStyle: 'extra-rounded',
	cornerSquareStyle: 'dot',
	cornerDotStyle: 'dot',
	gradientType: 'none',
	gradientStartColor: '#ffffff',
	gradientEndColor: '#ffffff',
	gradientDirection: 0,
	showUrl: true,
	transparentBackground: true,
	dropShadow: true,
	dropShadowColor: '#000000',
	dropShadowBlur: 10,
	dropShadowOpacity: 1.0,
	displayOnOverlay: 'none'
} as const;

/**
 * QR Code Utility Functions
 */
export const QR_UTILS = {
	/**
	 * Apply a color scheme to QR settings
	 */
	applyColorScheme: (settings: QRCodeSettings, schemeName: string): QRCodeSettings => {
		const scheme = QR_COLOR_SCHEMES[schemeName];
		if (!scheme) return settings;

		return {
			...settings,
			moduleColor: scheme.module,
			positionRingColor: scheme.ring,
			positionCenterColor: scheme.center,
			backgroundColor: scheme.bg,
			gradientStartColor: scheme.module,
			gradientEndColor: scheme.module
		};
	},

	/**
	 * Reset QR settings to defaults
	 */
	resetToDefaults: (): QRCodeSettings => ({
		...DEFAULT_QR_SETTINGS
	}),

	/**
	 * Generate URL text for QR code
	 */
	generateUrlText: (tag: string): string => {
		return tag ? `SUPERTIP.GG/${tag.toUpperCase()}` : 'SUPERTIP.GG';
	},

	/**
	 * Generate stream URL for QR code
	 */
	generateStreamUrl: (tag: string): string => {
		return `https://supertip.gg/${tag}`;
	}
} as const;
